import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import {
  Mail, MailOpen, Archive, Trash2, Phone, ChevronDown,
  ChevronUp, Loader2, RefreshCw,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [busy,     setBusy]     = useState(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    let q = supabase.from("contact_messages").select("*").order("created_at", { ascending: false });
    if (filter === "unread")   q = q.eq("is_read", false).eq("is_archived", false);
    if (filter === "archived") q = q.eq("is_archived", true);
    if (filter === "all")      q = q.eq("is_archived", false);
    const { data } = await q;
    setMessages(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markRead = async (msg, read) => {
    setBusy(msg.id);
    await supabase.from("contact_messages").update({ is_read: read }).eq("id", msg.id);
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: read } : m));
    setBusy(null);
  };

  const archive = async (msg) => {
    setBusy(msg.id);
    await supabase.from("contact_messages").update({ is_archived: true, is_read: true }).eq("id", msg.id);
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    if (expanded === msg.id) setExpanded(null);
    setBusy(null);
  };

  const del = async (msg) => {
    if (!window.confirm(`"${msg.name}" adlı mesajı kalıcı olarak silmek istiyor musunuz?`)) return;
    setBusy(msg.id);
    await supabase.from("contact_messages").delete().eq("id", msg.id);
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    if (expanded === msg.id) setExpanded(null);
    setBusy(null);
  };

  const toggle = async (id) => {
    const msg = messages.find(m => m.id === id);
    if (!msg) return;
    if (expanded !== id) {
      setExpanded(id);
      if (!msg.is_read) markRead(msg, true);
    } else {
      setExpanded(null);
    }
  };

  const FILTERS = [
    { id: "all",      label: "Gelen Kutusu" },
    { id: "unread",   label: "Okunmamış"    },
    { id: "archived", label: "Arşiv"         },
  ];

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[#C5A059] text-[11px] tracking-[0.2em] uppercase mb-1 font-medium">İletişim</p>
          <h1 className="text-[#F5F5F7] text-2xl font-light">
            Mesajlar
            {unreadCount > 0 && filter !== "archived" && (
              <span className="ml-2 bg-[#C5A059] text-black text-[11px] font-bold px-2 py-0.5 rounded-full align-middle">
                {unreadCount} yeni
              </span>
            )}
          </h1>
        </div>
        <button
          onClick={fetchMessages}
          className="flex items-center gap-2 px-3 py-2 text-[#666] hover:text-[#C5A059] border border-white/8 hover:border-[#C5A059]/30 rounded-sm transition-colors text-xs"
          title="Yenile"
        >
          <RefreshCw size={13} />
          <span className="hidden sm:inline">Yenile</span>
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-0 border-b border-white/6 mb-5">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => { setFilter(f.id); setExpanded(null); }}
            className={`px-4 sm:px-6 py-3 text-[13px] border-b-2 -mb-px transition-colors ${
              filter === f.id
                ? "text-[#C5A059] border-[#C5A059] font-medium"
                : "text-[#555] border-transparent hover:text-[#999]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#555] text-sm py-10">
          <Loader2 size={16} className="animate-spin text-[#C5A059]" />
          Yükleniyor...
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-20">
          <Mail size={32} className="text-[#2a2a2a] mx-auto mb-3" />
          <p className="text-[#555] text-sm">
            {filter === "archived" ? "Arşivde mesaj yok." : filter === "unread" ? "Okunmamış mesaj yok." : "Gelen kutunuz boş."}
          </p>
        </div>
      ) : (
        <div className="bg-[#0d0d0d] border border-white/6 overflow-hidden divide-y divide-white/5">
          {messages.map((msg) => {
            const isExp  = expanded === msg.id;
            const isBusy = busy === msg.id;
            return (
              <div key={msg.id} className={`transition-colors ${!msg.is_read ? "bg-[#C5A059]/4" : ""}`}>

                {/* ── Row button ── */}
                <button
                  onClick={() => toggle(msg.id)}
                  className="w-full text-left px-4 sm:px-5 py-4 flex items-start sm:items-center gap-3 hover:bg-white/3 transition-colors"
                >
                  {/* Unread dot */}
                  <div className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5 sm:mt-0"
                    style={{ background: msg.is_read ? "transparent" : "#C5A059" }} />

                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                    {msg.is_read
                      ? <MailOpen size={16} className="text-[#333]" />
                      : <Mail size={16} className="text-[#C5A059]" />}
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    {/* Name + date row */}
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className={`text-[14px] leading-tight ${msg.is_read ? "text-[#888] font-light" : "text-[#F5F5F7] font-medium"}`}>
                        {msg.name}
                      </p>
                      <p className="text-[11px] text-[#444] tabular-nums whitespace-nowrap flex-shrink-0">
                        {fmtDate(msg.created_at)}
                      </p>
                    </div>
                    {/* Email */}
                    <p className="text-[12px] text-[#555] mb-1 truncate">{msg.email}</p>
                    {/* Preview */}
                    <p className="text-[12px] text-[#444] truncate leading-relaxed">
                      {(msg.message || "").slice(0, 120)}
                    </p>
                  </div>

                  {/* Chevron */}
                  <div className="flex-shrink-0 text-[#444] ml-1">
                    {isExp ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </div>
                </button>

                {/* ── Expanded detail ── */}
                {isExp && (
                  <div className="px-4 sm:px-5 pb-5 pt-3 border-t border-white/5 bg-[#0a0a0a]">

                    {/* Contact info */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                      <div className="bg-[#111] border border-white/5 px-4 py-3 rounded-sm">
                        <p className="text-[10px] tracking-widest text-[#555] uppercase mb-1.5">Ad Soyad</p>
                        <p className="text-[14px] text-[#F5F5F7] font-light">{msg.name}</p>
                      </div>
                      <div className="bg-[#111] border border-white/5 px-4 py-3 rounded-sm">
                        <p className="text-[10px] tracking-widest text-[#555] uppercase mb-1.5">E-posta</p>
                        <a href={`mailto:${msg.email}`} className="text-[14px] text-[#C5A059] hover:underline font-light break-all">
                          {msg.email}
                        </a>
                      </div>
                      {msg.phone && (
                        <div className="bg-[#111] border border-white/5 px-4 py-3 rounded-sm">
                          <p className="text-[10px] tracking-widest text-[#555] uppercase mb-1.5">Telefon</p>
                          <a href={`tel:${msg.phone}`} className="text-[14px] text-[#C5A059] hover:underline font-light flex items-center gap-1.5">
                            <Phone size={12} />{msg.phone}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Message body */}
                    <div className="mb-5">
                      <p className="text-[10px] tracking-widest text-[#555] uppercase mb-2">Mesaj İçeriği</p>
                      <div className="bg-[#111] border-l-2 border-[#C5A059]/40 px-4 py-4 text-[13px] text-[#aaa] font-light leading-relaxed whitespace-pre-wrap rounded-sm">
                        {msg.message}
                      </div>
                    </div>

                    <p className="text-[11px] text-[#333] mb-5">{fmtDate(msg.created_at)}</p>

                    {/* Action buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => markRead(msg, !msg.is_read)}
                        disabled={!!busy}
                        className="flex items-center gap-2 px-4 py-2 text-[13px] bg-white/5 hover:bg-white/10 text-[#999] hover:text-[#F5F5F7] border border-white/8 rounded-sm transition-colors disabled:opacity-40"
                      >
                        {isBusy ? <Loader2 size={13} className="animate-spin" />
                          : msg.is_read ? <Mail size={13} /> : <MailOpen size={13} />}
                        {msg.is_read ? "Okunmadı İşaretle" : "Okundu İşaretle"}
                      </button>

                      {!msg.is_archived && (
                        <button
                          onClick={() => archive(msg)}
                          disabled={!!busy}
                          className="flex items-center gap-2 px-4 py-2 text-[13px] bg-white/5 hover:bg-white/10 text-[#999] hover:text-[#F5F5F7] border border-white/8 rounded-sm transition-colors disabled:opacity-40"
                        >
                          <Archive size={13} />
                          Arşivle
                        </button>
                      )}

                      <button
                        onClick={() => del(msg)}
                        disabled={!!busy}
                        className="flex items-center gap-2 px-4 py-2 text-[13px] bg-red-900/10 hover:bg-red-900/25 text-red-500/70 hover:text-red-400 border border-red-900/20 rounded-sm transition-colors disabled:opacity-40 ml-auto"
                      >
                        <Trash2 size={13} />
                        Sil
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
